import React, { useState, useEffect } from "react";
import cx from "classnames";
import * as styles from "./MobileHeader.module.scss";
import { Typography, Link, ExpandCollapse, TypographyV2 } from "@components";
import { useOnScroll } from "@helpers/Hooks";
import { StaticImage } from "gatsby-plugin-image";
import { getCurrentPage, LINKS, LinkType } from "@components/Link/Link";
import { Theme, useTheme } from "../DesktopHeader/DesktopHeader";
import { links, NavbarTopic } from "../HeaderLinks/Products";
import { links as resourcesLinks } from "../HeaderLinks/Resources";
import { links as companyLinks } from "../HeaderLinks/Company";
import { links as solutionsLinks } from "../HeaderLinks/Solutions";
import {
	LoginButton,
	LogInButtonV2,
	RequestDemoButtonV2,
} from "@components/Button_new/Button";
import { CaretDown } from "@assets/icons";

type Props = {
	defaultHeader?: Theme;
	fixedColor?: boolean; 
};

type ExpandItem = {
	title: string;
	links?: NavbarTopic[];
	id: number;
};

export default function MobileHeader({ defaultHeader = "dark",fixedColor }: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedLink, setSelectedLink] = useState<number | null>(null);
	const theme = useTheme(defaultHeader);

	const expandItems: ExpandItem[] = [
		{
			id: 1,
			title: "Products",
			links,
		},
		{
			id: 2,
			title: "Solutions",
			links: solutionsLinks,
		},
		{
			id: 3,
			title: "Resources",
			links: resourcesLinks,
		},
		{
			id: 4,
			title: "Pricing",
		},
		{
			id: 5,
			title: "Company",
			links: companyLinks,
		},
	];

	useEffect(() => {
		const handleResize = () => {
			if (isOpen) {
				setIsOpen(false);
			}
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	});

	const [scrolled, setScrolled] = useState(false);

	const updateScroll = () => {
		const SCROLLED_OFFSET = 10;
		const hasScrolled = window.scrollY > SCROLLED_OFFSET;
		if (hasScrolled) {
			setScrolled(hasScrolled);
		} else {
			setScrolled(false);
		}
	};

	useOnScroll(updateScroll);

	const onOpenMenu = () => {
		setIsOpen(true);
	};

	const onCloseMenu = () => {
		setIsOpen(false);
		document.body.style.position = "unset";
	};

	const openButton = (
		<svg
			onClick={onOpenMenu}
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M3 8.5H21M3 15.5H21"
				stroke="#5D6B98"
				stroke-width="1.5"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	);

	const closeButton = (
		<svg
			onClick={onCloseMenu}
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M18 6L6 18M6 6L18 18"
				stroke="#5D6B98"
				stroke-width="1.5"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	);

	const headerLink = (link: LinkType) => {
		return (
			<div>
				<Link link={link}>
					<TypographyV2
						variant="BODY_TEXT_MEDIUM"
						className={styles.pricingLink}
						weight="REGULAR"
				color="gray-700"

					>
						{LINKS[link]?.title}
					</TypographyV2>
				</Link>
			</div>
		);
	};
	const headerMenu = (
		<div
			className={cx(styles.headerMenu, {
				[styles.openHeaderMenu]: isOpen,
			})}
		>
			{expandItems.map(({ id, title, links }) => {
				if (links) {
					return (
						<ExpandCollapseLinks
							id={id}
							key={id}
							title={title}
							links={links}
							onClick={() => {
								if (selectedLink === id) {
									setSelectedLink(null);
								} else {
									setSelectedLink(id);
								}
							}}
							active={id == selectedLink}
						/>
					);
				} else {
					return (
						<div key={id} className={styles.linkMargin}>
							{headerLink("PRICING")}
						</div>
					);
				}
			})}
			<div className={styles.actions}>
				<LogInButtonV2 />
				<RequestDemoButtonV2 />
			</div>
		</div>
	);

	// open/close and logo
	const header = (
		<div
			className={cx(styles.header, {
				[styles.isOpen]: isOpen,
				[styles.scrolled]: scrolled,
				[styles.fixedColor]: fixedColor
			})}
		>
			<Link className={styles.logo} link="LANDING">
				<StaticImage
					loading="eager"
					src={"../img/Logo.png"}
					height={50}
					alt="Genemod logo"
				/>
			</Link>
			<div className={styles.headerButtons}>
				{isOpen ? closeButton : openButton}
			</div>
		</div>
	);

	return (
		<div className={styles.headerContainer}>
			{header}
			{headerMenu}
		</div>
	);
}

type ExpandCollapseLinksProps = {
	id: number;
	title: string;
	links: NavbarTopic[];
	active?: boolean;
	onClick: (id: number) => void;
};

const LinkContent = ({ icon, topics, description, title }: NavbarTopic) => {
	return (
		<div
			className={cx(styles.title, {
				[styles.titleHasTopics]: topics?.length,
				[styles.titleHasDescription]: !description,
			})}
		>
			{icon}
			<TypographyV2 weight="MEDIUM">{title}</TypographyV2>
			<TypographyV2
				variant="BODY_TEXT_SMALL"
				color="gray-500"
				style={{
					marginTop: 4,
				}}
			>
				{description}
			</TypographyV2>
		</div>
	);
};

const ExpandCollapseLinks = ({
	title,
	links,
	active,
	onClick,
	id,
}: ExpandCollapseLinksProps) => (
	<ExpandCollapse
		id={id}
		onClick={onClick}
		caret="DARK"
		expanded={active}
		expandedIconColor={"gray-700"}
		title={
			<TypographyV2
				className={styles.link}
				variant="BODY_TEXT_MEDIUM"
				weight={active ? "MEDIUM" :"REGULAR"}
				color="gray-700"
			>
				{title}
			</TypographyV2>
		}
	>
		<div className={styles.expandCollapseLinks}>
			{links.map((obj, index) => {
				const { title, description, topics, icon, to } = obj;
				return (
					<div key={index}>
						{to ? (
							<Link to={LINKS[to || "LANDING"].to}>
								<LinkContent
									title={title}
									description={description}
									topics={topics}
									icon={icon}
									id={index}
								/>
							</Link>
						) : (
							<LinkContent
								title={title}
								description={description}
								topics={topics}
								icon={icon}
								id={index}
							/>
						)}
						{topics?.length && (
							<div className={styles.content}>
								{topics?.map((topic, i) => {
									const { title, icon, description, to } =
										topic;
									return (
										<Link to={LINKS[to || "LANDING"].to}>
											<div
												key={i}
												className={cx(
													styles.expandableLink,
													{
														[styles.expandableLinkWithoutDescription]:
															!description,
													}
												)}
											>
												{icon}
												<span>
													<TypographyV2 weight="MEDIUM">
														{title}
													</TypographyV2>
												</span>
											</div>
										</Link>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	</ExpandCollapse>
);
